import { Checkbox, ConnectorFormDetails, Input, Label, SectionHeading } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";
import { readEffectiveSchoolIds, readProjectFilters, type ProjectFilters } from "./psScoping";
import { SchoolIdsInput } from "./SchoolIdsInput";
import { SinceDateInput } from "./SinceDateInput";

function SchoolIdsField(props: {
  schoolIds: string[];
  onCommit: (next: ProjectFilters) => void;
  baseFilters: ProjectFilters;
}) {
  return (
    <div className="mt-3">
      <Label htmlFor="ps-school-ids" className="form-label">
        School numbers
      </Label>
      <SchoolIdsInput
        id="ps-school-ids"
        value={props.schoolIds}
        override={props.schoolIds}
        onChange={(next) => props.onCommit({ ...props.baseFilters, schoolids: next })}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        PowerSchool school numbers — the Schools table&apos;s <code>School_Number</code> (e.g.
        4910), not the internal school ID. Leave empty to sync every school; with several schools,
        each table is fetched one school at a time.
      </p>
    </div>
  );
}

function SchoolYearStartDisclosure(props: {
  schoolYearStart: string;
  onCommit: (next: ProjectFilters) => void;
  baseFilters: ProjectFilters;
}) {
  return (
    <ConnectorFormDetails
      summary="Advanced — school year start"
      className="mt-3 rounded border border-border/50"
    >
      <Label htmlFor="ps-school-year-start" className="form-label">
        School year start (MM-DD)
      </Label>
      <Input
        id="ps-school-year-start"
        type="text"
        value={props.schoolYearStart}
        onChange={(e) => props.onCommit({ ...props.baseFilters, schoolYearStart: e.target.value })}
        placeholder="07-01"
        className="font-mono"
      />
      <p className="mt-1 text-xs text-muted-foreground">
        Overrides the default <code>07-01</code> when resolving <code>SCHOOLYEARSTART</code> tokens.
      </p>
    </ConnectorFormDetails>
  );
}

function ScopingSection(props: {
  schoolIds: string[];
  sinceDate: string;
  schoolYearStart: string;
  studentPoolFromReenrollments: boolean;
  hideAdvanced: boolean | undefined;
  onCommit: (next: ProjectFilters) => void;
  baseFilters: ProjectFilters;
}) {
  const {
    schoolIds,
    sinceDate,
    schoolYearStart,
    studentPoolFromReenrollments,
    hideAdvanced,
    onCommit,
    baseFilters,
  } = props;
  return (
    <div className="form-card mt-4">
      <SectionHeading size="md" as="h3">
        Project-level scoping
      </SectionHeading>
      <p className="mt-1 text-xs text-muted-foreground">
        Defaults that apply to every table unless overridden at the table or per-cadence level.
      </p>

      <SchoolIdsField schoolIds={schoolIds} onCommit={onCommit} baseFilters={baseFilters} />

      <div className="mt-4">
        <Label htmlFor="ps-since-date" className="form-label">
          Since date
        </Label>
        <SinceDateInput
          id="ps-since-date"
          value={sinceDate}
          override={sinceDate}
          schoolYearStart={schoolYearStart || undefined}
          tokens={["SCHOOLYEARSTART"]}
          onChange={(next) => onCommit({ ...baseFilters, sinceDate: next })}
        />
      </div>

      <div className="mt-4 flex items-start gap-2">
        <Checkbox
          id="ps-reenrollment-pool"
          checked={studentPoolFromReenrollments}
          onCheckedChange={(checked) =>
            onCommit({ ...baseFilters, studentPoolFromReenrollments: checked === true })
          }
        />
        <div>
          <Label htmlFor="ps-reenrollment-pool" className="text-sm">
            Include former students via reenrollments
          </Label>
          <p className="text-xs text-muted-foreground">
            Seeds the student pool from the <code>reenrollments</code> table, capturing students who
            transferred to other district schools (their current schoolid no longer matches). Pulls
            the full historical population for the scoped school(s). Default off.
          </p>
        </div>
      </div>

      {!hideAdvanced && (
        <SchoolYearStartDisclosure
          schoolYearStart={schoolYearStart}
          onCommit={onCommit}
          baseFilters={baseFilters}
        />
      )}
    </div>
  );
}

function DatasetOverridesSection(props: {
  connectorPrefix: string;
  bronzeDataset: string;
  silverDataset: string;
  onUpdate: (key: string, value: string) => void;
}) {
  const { connectorPrefix, bronzeDataset, silverDataset, onUpdate } = props;
  return (
    <ConnectorFormDetails
      summary="Advanced Options"
      className="mt-4 rounded border border-border/50"
    >
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="ps-bronze-dataset" className="form-label">
            Bronze Dataset
          </Label>
          <Input
            id="ps-bronze-dataset"
            type="text"
            value={bronzeDataset}
            onChange={(e) => onUpdate("bronzeDataset", e.target.value)}
            placeholder={`Default: bronze__${connectorPrefix}`}
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="ps-silver-dataset" className="form-label">
            Silver Dataset
          </Label>
          <Input
            id="ps-silver-dataset"
            type="text"
            value={silverDataset}
            onChange={(e) => onUpdate("silverDataset", e.target.value)}
            placeholder={`Default: silver__${connectorPrefix}`}
            className="font-mono"
          />
        </div>
      </div>
    </ConnectorFormDetails>
  );
}

function appendNonEmptyArray<T>(
  cleaned: ProjectFilters,
  key: keyof ProjectFilters,
  value: T[] | undefined,
) {
  if (value && value.length > 0) (cleaned as Record<string, unknown>)[key] = value;
}

function cleanProjectFilters(next: ProjectFilters): ProjectFilters {
  const cleaned: ProjectFilters = {};
  appendNonEmptyArray(cleaned, "schoolids", next.schoolids);
  if (next.sinceDate) cleaned.sinceDate = next.sinceDate;
  if (next.schoolYearStart) cleaned.schoolYearStart = next.schoolYearStart;
  // Emit only when true so the default-off flag never clutters config (and so a
  // scoping edit never silently strips a previously-set true — SIM-2274).
  if (next.studentPoolFromReenrollments) cleaned.studentPoolFromReenrollments = true;
  return cleaned;
}

function readSchedules(rawSchedules: unknown): Schedules {
  if (rawSchedules == null || typeof rawSchedules !== "object" || Array.isArray(rawSchedules)) {
    return {};
  }
  return rawSchedules as Schedules;
}

export function PowerSchoolsForm(props: ConnectorFormProps) {
  const tables = Array.isArray(props.vars.tables) ? (props.vars.tables as TableConfig[]) : [];
  const schedules = readSchedules(props.vars.schedules);

  const bronzeDataset = (props.vars.bronzeDataset as string) ?? "";
  const silverDataset = (props.vars.silverDataset as string) ?? "";

  const filters = readProjectFilters(props.vars);
  const schoolIds = readEffectiveSchoolIds(props.vars);
  const sinceDate = filters.sinceDate ?? "";
  const schoolYearStart = filters.schoolYearStart ?? "";
  const studentPoolFromReenrollments = filters.studentPoolFromReenrollments ?? false;

  function commitFilters(next: ProjectFilters) {
    const cleaned = cleanProjectFilters(next);
    // Drop legacy `vars.schoolid` once the user interacts with the new inputs.
    const { schoolid: _legacy, filters: _f, ...rest } = props.vars;
    const nextVars: Record<string, unknown> = { ...rest };
    if (Object.keys(cleaned).length > 0) nextVars.filters = cleaned;
    props.onVarsChange(nextVars);
    props.clearBanner();
  }

  function updateField(key: string, value: string) {
    const next = { ...props.vars };
    if (value) next[key] = value;
    else delete next[key];
    props.onVarsChange(next);
    props.clearBanner();
  }

  return (
    <>
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      <ScopingSection
        schoolIds={schoolIds}
        sinceDate={sinceDate}
        schoolYearStart={schoolYearStart}
        studentPoolFromReenrollments={studentPoolFromReenrollments}
        hideAdvanced={props.hideAdvanced}
        onCommit={commitFilters}
        baseFilters={filters}
      />
      {!props.hideAdvanced && (
        <DatasetOverridesSection
          connectorPrefix={props.connectorPrefix}
          bronzeDataset={bronzeDataset}
          silverDataset={silverDataset}
          onUpdate={updateField}
        />
      )}
    </>
  );
}
